import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type { ReportRouteGpsDto } from "@report/data/dto/report-route-gps.dto.ts";
import { ReportRouteGpsDtoSchema } from "@report/data/dto/report-route-gps.dto.ts";
import { ReportRouteGps } from "@report/domain/models/report-route-gps.ts";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";

@injectable()
export class ReportRouteGpsMapper {
    map(
        reportRouteGpsDto: ReportRouteGpsDto,
    ): Result<ReportRouteGps[], ModelError<typeof ReportRouteGps>> {
        const reportRouteGpsDtoParsed =
            ReportRouteGpsDtoSchema.safeParse(reportRouteGpsDto);

        if (!reportRouteGpsDtoParsed.success) {
            console.error(reportRouteGpsDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const reportsResults = Object.values(reportRouteGpsDtoParsed.data).map(
            (report) => {
                const stationId = Object.keys(report)[0];
                if (!stationId || !report[stationId])
                    return err(new NonRecuperableModelError());

                const roads = report[stationId].roads;
                const tracks = report[stationId].tracks;
                const station = report[stationId].station;

                const tracksMapped = tracks.map((track) => ({
                    timestamp: track.timestamp,
                    status: track.status,
                    position: track.position,
                    latitude: track.latitude,
                    longitude: track.longitude,
                    values: track.values,
                }));

                return ReportRouteGps.createWithRecovery({
                    stationReportSummary: {
                        icon: station.icon,
                        stationId: station.id,
                        stationName: `${station.name} (${station.technical_name})`,
                    },
                    roads,
                    tracks: tracksMapped,
                });
            },
        );

        if (reportsResults.some((result) => result.isErr())) {
            return err(new NonRecuperableModelError());
        }

        return ok(
            reportsResults
                .map((result) => result.unwrapOr(null))
                .filter(Boolean),
        );
    }
}
